/**
 * Opções alinhadas a `Conversation` (enum status/priority) e a `ActionService#change_status` /
 * `#change_priority` (primeiro elemento do array de params; prioridade usa string `'nil'` para nil).
 */

export const WORKFLOW_CONVERSATION_STATUS_OPTIONS = [
  { value: 'open', label: 'Aberta' },
  { value: 'resolved', label: 'Resolvida' },
  { value: 'pending', label: 'Pendente' },
  { value: 'snoozed', label: 'Adiada' },
];

export const WORKFLOW_CONVERSATION_PRIORITY_OPTIONS = [
  { value: 'nil', label: 'Nenhuma' },
  { value: 'low', label: 'Baixa' },
  { value: 'medium', label: 'Média' },
  { value: 'high', label: 'Alta' },
  { value: 'urgent', label: 'Urgente' },
];

const STATUS_VALUES = new Set(
  WORKFLOW_CONVERSATION_STATUS_OPTIONS.map(o => o.value)
);

const PRIORITY_VALUES = new Set(
  WORKFLOW_CONVERSATION_PRIORITY_OPTIONS.map(o => o.value)
);

export const normalizeWorkflowConversationStatus = raw => {
  const s = typeof raw === 'string' ? raw.trim() : '';
  return STATUS_VALUES.has(s) ? s : null;
};

export const normalizeWorkflowConversationPriority = raw => {
  const s = typeof raw === 'string' ? raw.trim() : '';
  return PRIORITY_VALUES.has(s) ? s : null;
};

export const workflowStatusLabel = value =>
  WORKFLOW_CONVERSATION_STATUS_OPTIONS.find(o => o.value === value)?.label ||
  (value ? String(value) : '');

export const workflowPriorityLabel = value =>
  WORKFLOW_CONVERSATION_PRIORITY_OPTIONS.find(o => o.value === value)?.label ||
  (value ? String(value) : '');

/** Minutos para ação Esperar — inteiro ≥ 1. */
export const normalizeWorkflowWaitMinutes = raw => {
  const n = Number(raw);
  if (!Number.isFinite(n)) return 5;
  const rounded = Math.round(n);
  return Math.max(1, rounded);
};

export const normalizeWorkflowWaitUnit = raw =>
  raw === 'seconds' ? 'seconds' : 'minutes';

export const normalizeWorkflowWaitValue = (raw, fallback = 5) => {
  const n = Number(raw);
  return Number.isFinite(n) ? Math.max(1, Math.round(n)) : fallback;
};

/** Resumo no cartão do nó «Esperar» no editor de fluxo. */
export const summarizeWorkflowWaitActionMinutes = flowNode => {
  if (
    !flowNode ||
    flowNode.type !== 'action' ||
    flowNode.data?.kind !== 'wait'
  ) {
    return '';
  }
  const unit = normalizeWorkflowWaitUnit(flowNode.data.wait_unit);
  const value = normalizeWorkflowWaitValue(
    flowNode.data.wait_value ?? flowNode.data.wait_minutes
  );
  return unit === 'seconds' ? `${value} s` : `${value} min`;
};

export const WORKFLOW_HTTP_METHOD_OPTIONS = [
  { value: 'GET', label: 'GET' },
  { value: 'POST', label: 'POST' },
  { value: 'PUT', label: 'PUT' },
  { value: 'PATCH', label: 'PATCH' },
  { value: 'DELETE', label: 'DELETE' },
  { value: 'HEAD', label: 'HEAD' },
  { value: 'OPTIONS', label: 'OPTIONS' },
];

/** Par nome/valor (query ou cabeçalhos), igual ao modelo de parâmetros do n8n. */
export const createWorkflowHttpPair = () => ({
  id:
    typeof globalThis.crypto !== 'undefined' &&
    typeof globalThis.crypto.randomUUID === 'function'
      ? globalThis.crypto.randomUUID()
      : `http-${Math.random().toString(36).slice(2, 11)}`,
  name: '',
  value: '',
});

export const normalizeWorkflowHttpPairs = raw => {
  if (!Array.isArray(raw)) {
    return [createWorkflowHttpPair()];
  }
  const rows = raw.map(item => ({
    id:
      item?.id !== null && item?.id !== undefined && String(item.id).length > 0
        ? String(item.id)
        : createWorkflowHttpPair().id,
    name: typeof item?.name === 'string' ? item.name : '',
    value:
      typeof item?.value === 'string'
        ? item.value
        : item?.value != null
          ? String(item.value)
          : '',
  }));
  return rows.length ? rows : [createWorkflowHttpPair()];
};

export const WORKFLOW_HTTP_AUTH_OPTIONS = [
  { value: 'none', label: 'Nenhuma' },
  { value: 'basic', label: 'Basic Auth' },
  { value: 'bearer', label: 'Bearer Token' },
  { value: 'header', label: 'Cabeçalho personalizado' },
];

const HTTP_AUTH_VALUES = new Set(WORKFLOW_HTTP_AUTH_OPTIONS.map(o => o.value));

export const normalizeWorkflowHttpAuthType = raw => {
  const s = typeof raw === 'string' ? raw.trim() : '';
  return HTTP_AUTH_VALUES.has(s) ? s : 'none';
};

export const normalizeWorkflowHttpCredString = raw =>
  typeof raw === 'string' ? raw : '';

export const WORKFLOW_HTTP_BODY_CONTENT_OPTIONS = [
  { value: 'json', label: 'Definir abaixo' },
  { value: 'raw', label: 'Texto cru' },
  { value: 'form_urlencoded', label: 'Form URL-encoded' },
];

const HTTP_BODY_CT = new Set(
  WORKFLOW_HTTP_BODY_CONTENT_OPTIONS.map(o => o.value)
);

export const normalizeWorkflowHttpBodyContentType = raw => {
  const s = typeof raw === 'string' ? raw.trim() : '';
  return HTTP_BODY_CT.has(s) ? s : 'json';
};

const HTTP_METHOD_VALUES = new Set(
  WORKFLOW_HTTP_METHOD_OPTIONS.map(o => o.value)
);

export const normalizeWorkflowHttpMethod = raw => {
  const s =
    typeof raw === 'string'
      ? raw
          .trim()
          .toUpperCase()
          .replace(/^HTTP\s+/i, '')
      : '';
  return HTTP_METHOD_VALUES.has(s) ? s : 'GET';
};

export const normalizeWorkflowHttpUrl = raw =>
  typeof raw === 'string' ? raw.trim() : '';

export const normalizeWorkflowHttpBody = raw =>
  typeof raw === 'string' ? raw : '';

/** Corpo serializado a partir das linhas «chave / valor» (modelo parecido com o n8n). */
export function serializeWorkflowHttpBodyFromFields(contentType, fields) {
  const rows = Array.isArray(fields) ? fields : [];
  const named = rows.filter(r => String(r?.name || '').trim() !== '');

  if (contentType === 'json') {
    if (named.length === 0) {
      const rawCandidate = rows
        .map(r => String(r?.value ?? '').trim())
        .find(v => v.length > 0);
      if (rawCandidate) {
        try {
          JSON.parse(rawCandidate);
          return rawCandidate;
        } catch {
          /* use fallthrough */
        }
      }
      return '';
    }
    const obj = {};
    named.forEach(({ name, value }) => {
      const key = String(name).trim();
      const vRaw = typeof value === 'string' ? value : String(value ?? '');
      const trimmed = vRaw.trim();
      let normalized = vRaw;
      if (trimmed === 'true') normalized = true;
      else if (trimmed === 'false') normalized = false;
      else if (trimmed === 'null') normalized = null;
      else if (
        trimmed !== '' &&
        !Number.isNaN(Number(trimmed)) &&
        /^-?\d+(\.\d+)?$/u.test(trimmed)
      )
        normalized = Number(trimmed);
      else {
        try {
          normalized = JSON.parse(trimmed);
        } catch {
          normalized = vRaw;
        }
      }
      obj[key] = normalized;
    });
    return JSON.stringify(obj);
  }

  if (contentType === 'form_urlencoded') {
    const p = new URLSearchParams();
    named.forEach(({ name, value }) => {
      p.append(
        String(name).trim(),
        typeof value === 'string' ? value : String(value ?? '')
      );
    });
    return p.toString();
  }

  return '';
}

function inferHttpBodyFieldsFromLegacy(contentType, bodyString) {
  const s = normalizeWorkflowHttpBody(bodyString);
  if (contentType === 'form_urlencoded' && s.trim()) {
    try {
      const raw = s.trim().startsWith('?') ? s.trim().slice(1) : s.trim();
      const params = new URLSearchParams(raw);
      const pairs = [];
      params.forEach((value, name) => {
        pairs.push({ ...createWorkflowHttpPair(), name, value });
      });
      if (pairs.length > 0) return pairs;
    } catch {
      /* fallthrough */
    }
  }
  if (contentType === 'json' && s.trim()) {
    try {
      const parsed = JSON.parse(s);
      if (
        parsed !== null &&
        typeof parsed === 'object' &&
        !Array.isArray(parsed)
      ) {
        const entries = Object.entries(parsed);
        if (entries.length === 0) return normalizeWorkflowHttpPairs(undefined);
        return entries.map(([name, val]) => ({
          ...createWorkflowHttpPair(),
          name,
          value: typeof val === 'string' ? val : JSON.stringify(val),
        }));
      }
      return [{ ...createWorkflowHttpPair(), name: '', value: s }];
    } catch {
      return [{ ...createWorkflowHttpPair(), name: '', value: s }];
    }
  }
  return normalizeWorkflowHttpPairs(undefined);
}

const METHOD_IMPLIES_BODY_DEFAULT = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

/**
 * Estado completo do nó HTTP (grafo persistido).
 * Inspirado nos painéis «HTTP Request» do n8n: URL, auth, query, headers, corpo.
 */
export function normalizeWorkflowHttpRequestFields(d = {}) {
  const http_method = normalizeWorkflowHttpMethod(d.http_method);
  const http_url = normalizeWorkflowHttpUrl(d.http_url);
  const http_query_params = normalizeWorkflowHttpPairs(d.http_query_params);
  const http_headers = normalizeWorkflowHttpPairs(d.http_headers);
  const http_auth_type = normalizeWorkflowHttpAuthType(d.http_auth_type);
  const http_basic_user = normalizeWorkflowHttpCredString(d.http_basic_user);
  const http_basic_password = normalizeWorkflowHttpCredString(
    d.http_basic_password
  );
  const http_bearer_token = normalizeWorkflowHttpCredString(
    d.http_bearer_token
  );
  const http_generic_header_name = normalizeWorkflowHttpCredString(
    d.http_generic_header_name
  );
  const http_generic_header_value = normalizeWorkflowHttpCredString(
    d.http_generic_header_value
  );

  const http_body_content_type = normalizeWorkflowHttpBodyContentType(
    d.http_body_content_type
  );

  let http_body_fields;
  if (Array.isArray(d.http_body_fields)) {
    http_body_fields = normalizeWorkflowHttpPairs(d.http_body_fields);
  } else {
    http_body_fields = inferHttpBodyFieldsFromLegacy(
      http_body_content_type,
      d.http_body
    );
  }

  let http_send_body;
  if (typeof d.http_send_body === 'boolean') {
    http_send_body = d.http_send_body;
  } else {
    const legacyBodyLen = normalizeWorkflowHttpBody(d.http_body).length;
    if (http_body_content_type === 'raw' && legacyBodyLen > 0) {
      http_send_body = true;
    } else if (http_body_content_type !== 'raw') {
      const hasNamed = http_body_fields.some(
        r => String(r.name || '').trim() !== ''
      );
      const ser = serializeWorkflowHttpBodyFromFields(
        http_body_content_type,
        http_body_fields
      );
      const meaningful = ser.length > 0;
      http_send_body =
        meaningful || hasNamed || METHOD_IMPLIES_BODY_DEFAULT.has(http_method);
    } else if (legacyBodyLen > 0) {
      http_send_body = true;
    } else {
      http_send_body = METHOD_IMPLIES_BODY_DEFAULT.has(http_method);
    }
  }

  let http_body;
  if (http_body_content_type === 'raw') {
    http_body = normalizeWorkflowHttpBody(d.http_body);
  } else {
    http_body = serializeWorkflowHttpBodyFromFields(
      http_body_content_type,
      http_body_fields
    );
  }

  let http_enable_auth;
  if (typeof d.http_enable_auth === 'boolean') {
    http_enable_auth = d.http_enable_auth;
  } else {
    http_enable_auth =
      http_auth_type !== 'none' ||
      !!http_basic_user ||
      !!http_basic_password ||
      !!http_bearer_token ||
      !!http_generic_header_name.trim();
  }

  let http_enable_headers;
  if (typeof d.http_enable_headers === 'boolean') {
    http_enable_headers = d.http_enable_headers;
  } else {
    http_enable_headers = http_headers.some(
      h => String(h.name || '').trim() !== ''
    );
  }

  let http_enable_query_params;
  if (typeof d.http_enable_query_params === 'boolean') {
    http_enable_query_params = d.http_enable_query_params;
  } else {
    http_enable_query_params = http_query_params.some(
      q => String(q.name || '').trim() !== ''
    );
  }

  return {
    http_method,
    http_url,
    http_query_params,
    http_headers,
    http_auth_type,
    http_basic_user,
    http_basic_password,
    http_bearer_token,
    http_generic_header_name,
    http_generic_header_value,
    http_send_body,
    http_body_content_type,
    http_body,
    http_body_fields,
    http_enable_auth,
    http_enable_headers,
    http_enable_query_params,
  };
}

/** Resumo no cartão do nó «HTTP» no editor de fluxo. */
export const summarizeWorkflowHttpRequest = flowNode => {
  if (
    !flowNode ||
    flowNode.type !== 'action' ||
    flowNode.data?.kind !== 'http_request'
  ) {
    return '';
  }
  const method = normalizeWorkflowHttpMethod(flowNode.data.http_method);
  const url = normalizeWorkflowHttpUrl(flowNode.data.http_url);
  if (!url) return `${method} · URL…`;
  const max = 36;
  const u = url.length > max ? `${url.slice(0, max)}…` : url;
  return `${method} ${u}`;
};

export const normalizeWorkflowAiAgentFields = d => ({
  ai_openai_token:
    typeof d.ai_openai_token === 'string' ? d.ai_openai_token : '',
  ai_system_prompt:
    typeof d.ai_system_prompt === 'string' ? d.ai_system_prompt : '',
  ai_input: typeof d.ai_input === 'string' ? d.ai_input : '',
  ai_tool_node_ids: Array.isArray(d.ai_tool_node_ids)
    ? d.ai_tool_node_ids.map(String)
    : [],
});

const newConversationCustomAttrPairId =
  typeof globalThis.crypto !== 'undefined' &&
  typeof globalThis.crypto.randomUUID === 'function'
    ? () => globalThis.crypto.randomUUID()
    : () => `cca-${Math.random().toString(36).slice(2, 11)}`;

export const createWorkflowConversationCustomAttributePair = () => ({
  id: newConversationCustomAttrPairId(),
  attribute_key: '',
  value: '',
});

export function normalizeWorkflowConversationCustomAttributePairs(raw) {
  if (!Array.isArray(raw)) {
    return [createWorkflowConversationCustomAttributePair()];
  }
  const rows = raw.map(row => ({
    id:
      row?.id !== null && row?.id !== undefined && String(row.id).length > 0
        ? String(row.id)
        : newConversationCustomAttrPairId(),
    attribute_key:
      typeof row?.attribute_key === 'string' ? row.attribute_key.trim() : '',
    value:
      typeof row?.value === 'string'
        ? row.value
        : row?.value != null
          ? String(row.value)
          : '',
  }));
  return rows.length ? rows : [createWorkflowConversationCustomAttributePair()];
}

export function normalizeWorkflowConversationCustomAttributesFields(d = {}) {
  return {
    conversation_custom_attribute_pairs:
      normalizeWorkflowConversationCustomAttributePairs(
        d.conversation_custom_attribute_pairs
      ),
  };
}

/** Resumo no cartão «Atributos da conversa». */
export const summarizeWorkflowConversationCustomAttributes = flowNode => {
  if (
    !flowNode ||
    flowNode.type !== 'action' ||
    flowNode.data?.kind !== 'set_custom_attributes'
  ) {
    return '';
  }
  const pairs = normalizeWorkflowConversationCustomAttributePairs(
    flowNode.data.conversation_custom_attribute_pairs
  );
  const named = pairs.filter(
    row => String(row.attribute_key || '').trim().length > 0
  );
  if (!named.length) return '';
  if (named.length === 1) {
    const k = String(named[0].attribute_key);
    const v = String(named[0].value || '').trim();
    const kShort = k.length > 16 ? `${k.slice(0, 16)}…` : k;
    const vShort = v.length > 22 ? `${v.slice(0, 22)}…` : v;
    return `${kShort}${vShort ? `: ${vShort}` : '(vazio)'}`;
  }
  return `${named.length} atributos`;
};
